import { NO_DATA, formatAxis, formatPercent, periodShortLabel, toNumber } from '@/utils/format';
import { font, v } from '@/theme/tokens';
import type { PnlSourceRow } from '@/types/schemas';

/** The importer's classification of a sheet row. */
const KIND = {
  percentage: 'PERCENTAGE',
  header: 'HEADER',
  informational: 'INFORMATIONAL',
} as const;

/** Totals and derived lines carry the sheet's own emphasis. */
const STRONG_KINDS = new Set(['AGGREGATE', 'DERIVED_FORMULA']);

/** "2026-07" → 7 */
export const monthOf = (key: string) => Number(key.split('-')[1]);

export const YTD_LABEL = 'Итоги года';

/** "Янв–Июл" — the span the totals column actually covers. */
export function ytdRangeOf(periods: string[]): string {
  if (periods.length === 0) return '';
  return `${periodShortLabel(monthOf(periods[0]))}–${periodShortLabel(monthOf(periods[periods.length - 1]))}`;
}

interface Props {
  rows: PnlSourceRow[];
  periods: string[];
  /** "2026-07" — the month the rest of the app is looking at. */
  selectedKey?: string | null;
  /** Opens the drill-down. Only mapped rows have anything behind them. */
  onOpenValue?: (categoryId: string, label: string) => void;
}

/**
 * A confirmed worksheet, rendered as it is.
 *
 * Shared by «Отчёт ОПУ» and the Гераци report table so both read the same:
 * the sheet's own order, its own captions, its own totals — nothing
 * regrouped, renamed or recomputed here. The component only decides how a
 * value is *formatted*, never what it is.
 */
export function SourceReportTable({ rows, periods, selectedKey = null, onOpenValue }: Props) {
  const ytdRange = ytdRangeOf(periods);

  const cell = (row: PnlSourceRow, value: number | null) => {
    if (value === null) return NO_DATA;
    return row.row_kind === KIND.percentage ? formatPercent(value) : formatAxis(value);
  };

  return (
    <div style={{ overflowX: 'auto' }} className="scroll-area">
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
        <thead>
          <tr>
            <th style={{ position: 'sticky', left: 0, zIndex: 2, background: v('surface'), textAlign: 'left', padding: '10px 12px', borderBottom: `2px solid ${v('line')}`, fontSize: 12, color: v('ink-muted'), minWidth: 260 }}>
              Статья
            </th>
            <th style={{ textAlign: 'left', padding: '10px 10px', borderBottom: `2px solid ${v('line')}`, fontSize: 12, color: v('ink-muted'), minWidth: 52 }}>
              Код
            </th>
            {periods.map((key) => {
              const active = key === selectedKey;
              return (
                <th
                  key={key}
                  style={{
                    textAlign: 'right', padding: '10px 12px',
                    borderBottom: `2px solid ${v('line')}`, fontSize: 12,
                    color: active ? v('accent-ink') : v('ink-muted'),
                    background: active ? v('accent-soft') : 'transparent',
                    minWidth: 84,
                  }}
                >
                  {periodShortLabel(monthOf(key))}
                </th>
              );
            })}
            {/* The totals column: an accent rule and accent type, so it reads
                as the summary — never confused with the selected month, which
                keeps its own soft highlight. */}
            <th
              style={{
                textAlign: 'right', padding: '10px 12px',
                borderBottom: `2px solid ${v('accent')}`,
                borderLeft: `2px solid ${v('accent')}`,
                fontSize: 12, fontWeight: 700, color: v('accent-ink'),
                minWidth: 104, whiteSpace: 'nowrap',
              }}
            >
              {YTD_LABEL}
              {ytdRange && (
                <div style={{ fontSize: 10.5, fontWeight: 500, color: v('ink-muted'), marginTop: 1 }}>
                  {ytdRange}
                </div>
              )}
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => {
            const isRatio = row.row_kind === KIND.percentage;
            const muted =
              isRatio || row.row_kind === KIND.header || row.row_kind === KIND.informational;
            const strong = STRONG_KINDS.has(row.row_kind) && row.level === 0;

            return (
              <tr key={row.row_index} style={{ background: strong ? v('panel') : 'transparent' }}>
                <td
                  style={{
                    position: 'sticky', left: 0, zIndex: 1,
                    background: strong ? v('panel') : v('surface'),
                    padding: '7px 12px',
                    paddingLeft: 12 + row.level * 18,
                    borderBottom: `1px solid ${v('line-soft')}`,
                    fontSize: 13,
                    fontWeight: strong ? 700 : row.level === 0 ? 600 : 500,
                    fontStyle: isRatio ? 'italic' : 'normal',
                    color: muted ? v('ink-muted') : v('ink'),
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.category_id && onOpenValue ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => onOpenValue(row.category_id!, row.label)}
                      onKeyDown={(e) => e.key === 'Enter' && onOpenValue(row.category_id!, row.label)}
                      style={{ cursor: 'pointer' }}
                    >
                      {row.label}
                    </span>
                  ) : (
                    <span>{row.label}</span>
                  )}
                </td>

                <td
                  style={{
                    padding: '7px 10px', borderBottom: `1px solid ${v('line-soft')}`,
                    fontFamily: font.mono, fontSize: 11.5, color: v('ink-faint'),
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.excel_code ?? ''}
                </td>

                {periods.map((key) => {
                  const value = toNumber(row.values[key] ?? null);
                  const active = key === selectedKey;
                  return (
                    <td
                      key={key}
                      style={{
                        textAlign: 'right', padding: '7px 12px',
                        borderBottom: `1px solid ${v('line-soft')}`,
                        fontFamily: font.mono, fontSize: 12.5,
                        color: value === null
                          ? v('ink-faint')
                          : muted ? v('ink-muted') : active ? v('ink') : v('ink-sub'),
                        background: active ? v('accent-soft') : 'transparent',
                        fontWeight: strong ? 600 : 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cell(row, value)}
                    </td>
                  );
                })}

                <td
                  style={{
                    textAlign: 'right', padding: '7px 12px',
                    borderBottom: `1px solid ${v('line-soft')}`,
                    fontFamily: font.mono, fontSize: 12.5,
                    fontWeight: strong ? 800 : 600,
                    borderLeft: `2px solid ${v('accent')}`,
                    color: muted ? v('ink-muted') : v('accent-ink'),
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cell(row, toNumber(row.ytd))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
